import { Button, confirm, FormRoot, Label, RHF, zodResolver } from "@devhop/ui";
import { useMemo } from "react";
import { z } from "zod";
import { zfd } from "zod-form-data";
import type {
  CreateAssignmentFormDTO,
  UpdateAssignmentFormDTO,
} from "@/modules/assign-position/domain/contracts";
import {
  usePositionSalaryBandsQuery,
  usePositionsQuery,
} from "@/modules/positions/presentation/api/queries";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

/** Radix Select ບໍ່ຮອງຮັບ value ວ່າງ — ໃຊ້ sentinel ແປງເປັນ "" ຕອນສົ່ງຟອມ */
const SALARY_GRADE_AUTO_SELECT_VALUE = "__salary_grade_auto__";
import { useCreateAssignment, useUpdateAssignment } from "../api/queries";

type PositionItem = { id: string; name: string };

const AssignmentFormSchema = zfd.formData({
  positionId: zfd.text(z.string().min(1, "ກະລຸນາເລືອກຕໍາແໜ່ງ")),
  salaryGradeId: zfd.text(z.string().optional()),
  notes: zfd.text(z.string().nullable().optional()),
});

type AssignmentFormValues = z.infer<typeof AssignmentFormSchema>;

export function AssignmentForm({
  userId,
  currentPositionId,
  currentDepartmentId,
  onSubmit,
}: {
  userId: string;
  currentPositionId?: string | null;
  currentDepartmentId?: string | null;
  onSubmit: () => void;
}) {
  const methods = RHF.useForm<AssignmentFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver inference
    resolver: zodResolver(AssignmentFormSchema) as unknown as any,
    defaultValues: {
      positionId: currentPositionId ?? "",
      salaryGradeId: "",
      notes: "",
    },
  });

  const positionId = methods.watch("positionId") ?? "";
  const trimmedPositionId =
    typeof positionId === "string" ? positionId.trim() : "";
  const salaryScale = usePositionSalaryBandsQuery(trimmedPositionId, {
    enabled: trimmedPositionId !== "",
  });

  const createAssignment = useCreateAssignment();
  const updateAssignment = useUpdateAssignment(userId);
  const isUpdate = !!currentPositionId;
  const positions = usePositionsQuery({ limit: 1000, offset: 0 });

  const positionMap = useMemo(() => {
    const map = new Map<string, { departmentId: string; title: string }>();
    positions.data?.data.forEach(
      (pos: { id: string; departmentId: string; title: string }) => {
        map.set(pos.id, {
          departmentId: pos.departmentId,
          title: pos.title,
        });
      },
    );
    return map;
  }, [positions.data?.data]);

  const handleSubmit = async (values: AssignmentFormValues) => {
    const newPosition = positionMap.get(values.positionId);
    const isCrossDepartment =
      isUpdate &&
      currentDepartmentId &&
      newPosition &&
      newPosition.departmentId !== currentDepartmentId;

    if (isUpdate || isCrossDepartment) {
      const title = isUpdate ? "ປ່ຽນຕໍາແໜ່ງ" : "ກໍາຫນົດຕໍາແໜ່ງ";
      let description = isUpdate
        ? "ທ່ານແນ່ໃຈບໍ່ວ່າຈະປ່ຽນຕໍາແໜ່ງ?"
        : "ທ່ານແນ່ໃຈບໍ່ວ່າຈະກໍາຫນົດຕໍາແໜ່ງ?";

      if (isCrossDepartment) {
        description += "\n\n⚠️ ການຍ້າຍຂ້າມພະແນກອາດຈະກະທົບຕໍ່ສິດທິ / workflow";
      }

      const ok = await confirm({
        title,
        description,
        actionText: isUpdate ? "ປ່ຽນຕໍາແໜ່ງ" : "ກໍາຫນົດຕໍາແໜ່ງ",
      });

      if (!ok) return;
    }

    const salaryGradeTrim =
      typeof values.salaryGradeId === "string"
        ? values.salaryGradeId.trim()
        : "";
    const salaryGradeIdPayload =
      salaryGradeTrim !== "" ? salaryGradeTrim : undefined;

    if (isUpdate) {
      const payload: UpdateAssignmentFormDTO = {
        positionId: values.positionId,
        notes: values.notes ?? null,
        ...(salaryGradeIdPayload !== undefined
          ? { salaryGradeId: salaryGradeIdPayload }
          : {}),
      };
      await updateAssignment.mutateAsync(payload);
    } else {
      const payload = {
        userId,
        positionId: values.positionId,
        isPrimary: true,
        notes: values.notes ?? null,
        ...(salaryGradeIdPayload !== undefined
          ? { salaryGradeId: salaryGradeIdPayload }
          : {}),
      } as CreateAssignmentFormDTO;
      await createAssignment.mutateAsync(payload);
    }
    onSubmit();
  };

  const gradeRows = useMemo(
    () =>
      Array.isArray(salaryScale.data?.grades) ? salaryScale.data.grades : [],
    [salaryScale.data?.grades],
  );

  const salaryGradeOptions = useMemo(
    () => [
      {
        value: SALARY_GRADE_AUTO_SELECT_VALUE,
        label: "ໃຫ້ລະບົບເລືອກໃຫ້",
      },
      ...gradeRows.map((g: { id: string; code: string; label: string }) => ({
        value: g.id,
        label: `${g.code} — ${g.label}`,
      })),
    ],
    [gradeRows],
  );

  return (
    <FormRoot<AssignmentFormValues>
      methods={methods as never}
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div className="min-w-0 max-w-full">
        <FormInfiniteCombobox<PositionItem>
          name="positionId"
          label="ຕໍາແໜ່ງ"
          requiredMark
          queryKey={["positions", "assignment"]}
          queryFn={(args) =>
            fetchLookupForInfinite(`${config.apiUrl}/positions/lookup`, args)
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/positions/lookup`, id)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ເລືອກຕໍາແໜ່ງ"
        />
      </div>

      {trimmedPositionId !== "" ? (
        <div className="space-y-2">
          <Label htmlFor="assignment-salary-grade">
            ລະດັບເງິນເດືອນຕໍາແໜ່ງ
          </Label>
          <RHF.Controller
            name="salaryGradeId"
            control={methods.control}
            render={({ field }) => (
              <SimpleSelect
                id="assignment-salary-grade"
                className="h-10 w-full"
                options={salaryGradeOptions}
                value={
                  !field.value || field.value === ""
                    ? SALARY_GRADE_AUTO_SELECT_VALUE
                    : field.value
                }
                onValueChange={(val) =>
                  field.onChange(
                    val === SALARY_GRADE_AUTO_SELECT_VALUE ? "" : val,
                  )
                }
                disabled={salaryScale.isLoading}
                placeholder="ໃຫ້ລະບົບເລືອກໃຫ້"
              />
            )}
          />
          {salaryScale.isLoading ? (
            <p className="text-muted-foreground text-xs">
              ກໍາລັງໂຫຼດລະດັບເງິນເດືອນ...
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          isLoading={createAssignment.isPending || updateAssignment.isPending}
        >
          {isUpdate ? "ປ່ຽນຕໍາແໜ່ງ" : "ກໍາຫນົດຕໍາແໜ່ງ"}
        </Button>
      </div>
    </FormRoot>
  );
}
